import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";

// The list's `ordering` values; the table's headers can also sort by status.
const SORT_OPTIONS = [
  ["created", "oldest"],
  ["-created", "newest"],
  ["name", "name_asc"],
  ["-name", "name_desc"],
];
const HEADER_SORTS = { status__name: "status_asc", "-status__name": "status_desc" };

// aria-disabled, not disabled: the button just pressed keeps the keyboard focus while the page loads, and
// when it becomes unavailable (the last page reached). 44px on phones.
const pageButton = (off) =>
  `inline-flex h-11 w-11 items-center justify-center bg-white transition duration-200 sm:h-9 sm:w-9 ${focusRing} focus-visible:z-10 ${
    off ? "cursor-not-allowed text-slate-300" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
  }`;

/** First / previous / next / last page; nothing for a single page. `label` names the landmark (two pagers differ). */
export function Pager({ page, pageCount, onPage, disabled = false, label, className = "" }) {
  const [t] = useTranslation();
  if (!(pageCount > 1)) return null;
  const buttons = [
    [1, page <= 1, "table.first_page", ChevronDoubleLeftIcon],
    [page - 1, page <= 1, "table.previous_page", ChevronLeftIcon],
    [page + 1, page >= pageCount, "table.next_page", ChevronRightIcon],
    [pageCount, page >= pageCount, "table.last_page", ChevronDoubleRightIcon],
  ];
  return (
    <nav aria-label={label || t("table.pagination")} className={`inline-flex divide-x divide-slate-200 overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-slate-200 ${className}`}>
      {buttons.map(([target, unavailable, name, Icon]) => (
        <button
          key={name}
          type="button"
          aria-disabled={disabled || unavailable}
          onClick={() => {
            if (!disabled && !unavailable) onPage(target);
          }}
          aria-label={t(name)}
          title={t(name)}
          className={pageButton(disabled || unavailable)}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      ))}
    </nav>
  );
}

/**
 * Over a list: how many items match ("22 questions · page 1 of 3"), the sort order and the pages. `ordering`
 * is the list's ordering value. Question lists get their texts by default; other lists pass `countText` and
 * `sortOptions` ([{value, label}]); without `onOrderingChange` there is no sort. While `loading`, the pages
 * wait: the count is the old one. `pager={false}` leaves the pages to the list's own Pager under it (one pager on
 * the screen); phones always page under the list only. `action` is a quiet button of the list's own, before the
 * sort (on phones an icon alone, named by its title).
 */
export default function ResultsBar({
  count,
  countText,
  page,
  pageCount,
  ordering,
  onOrderingChange,
  sortOptions,
  onPage,
  loading = false,
  pager = true,
  action = null,
}) {
  const [t] = useTranslation();
  const id = useId();
  const questionSorts = HEADER_SORTS[ordering] ? [...SORT_OPTIONS, [ordering, HEADER_SORTS[ordering]]] : SORT_OPTIONS;
  const options = sortOptions || questionSorts.map(([value, key]) => ({ value, label: t(`question_list.sort_options.${key}`) }));

  return (
    // Phones keep one line, so the line stays 44px tall: the count line takes the room the controls leave (its page
    // part wraps inside its box), never less than the count itself ("6 booklets", never cut); when even that does not
    // fit, the sort select narrows instead (its text is cut inside the select).
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 max-sm:flex-nowrap max-sm:gap-x-3">
      <p className="text-sm text-slate-500 max-sm:grow max-sm:basis-0" aria-live="polite">
        <span className="whitespace-nowrap text-base font-semibold text-slate-900">
          {countText ?? t("question_list.count", { count: count || 0 })}
        </span>
        {pageCount > 1 && <span> · {t("question_list.page_of", { page, pages: pageCount })}</span>}
      </p>
      {/* relative: the phones' sr-only label stays inside the page's scroll box. */}
      <div className="relative flex min-w-0 flex-wrap items-center gap-2">
        {action}
        {onOrderingChange && (
          <>
            <label htmlFor={`${id}-sort`} className="text-sm text-slate-500 max-sm:sr-only">
              {t("question_list.sort")}
            </label>
            <select
              id={`${id}-sort`}
              value={ordering}
              onChange={(e) => onOrderingChange(e.target.value)}
              className="h-11 min-w-0 truncate rounded-lg border-slate-300 py-0 pl-3 pr-9 text-sm text-slate-900 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:h-9"
            >
              {options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </>
        )}
        {/* Phones page under the list only. */}
        {pager && <Pager page={page} pageCount={pageCount} onPage={onPage} disabled={loading} className="max-sm:hidden" />}
      </div>
    </div>
  );
}
